// =============================================================================
// GroupedVariantTable — Size → Color grouping for Portal product pricing
// =============================================================================
// Inserts a Size header row above each color cluster. Axis names are matched
// case-insensitively ("size" / "Size" / "SIZE"). Variants with no Size land
// under Ungrouped. Optional `columnAfterPrice` is rendered by the caller
// inside each data row (bulk chips / Edit bulk).
// =============================================================================

import { useMemo, useState, type ReactNode } from 'react';
import { groupBySize } from '@tkpod/shared';

export interface GroupedVariantTableProps<T extends { id: string }> {
  variants: T[];
  getOptions: (variant: T) => unknown;
  colSpan: number;
  renderRow: (variant: T) => ReactNode;
  renderGroupActions?: (group: { sizeLabel: string; items: T[] }) => ReactNode;
}

export function GroupedVariantTable<T extends { id: string }>({
  variants,
  getOptions,
  colSpan,
  renderRow,
  renderGroupActions,
}: GroupedVariantTableProps<T>) {
  const groups = useMemo(
    () => groupBySize(variants, getOptions),
    [variants, getOptions],
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  if (groups.length <= 1 && groups[0]?.sizeKey === '') {
    return (
      <tbody className="divide-y divide-border">
        {variants.map((v) => renderRow(v))}
      </tbody>
    );
  }

  return (
    <>
      {groups.map((group) => {
        const isCollapsed = collapsed.has(group.sizeKey);
        return (
          <tbody key={group.sizeKey || 'ungrouped'} className="divide-y divide-border">
            <tr className="bg-surface-secondary/60">
              <td colSpan={colSpan} className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="text-xs font-semibold text-text-primary"
                    onClick={() =>
                      setCollapsed((prev) => {
                        const next = new Set(prev);
                        if (next.has(group.sizeKey)) next.delete(group.sizeKey);
                        else next.add(group.sizeKey);
                        return next;
                      })
                    }
                    aria-expanded={!isCollapsed}
                  >
                    {isCollapsed ? '▸' : '▾'} {group.sizeLabel}
                    <span className="ml-2 font-normal text-text-tertiary">
                      {group.items.length} variants
                    </span>
                  </button>
                  {renderGroupActions?.(group)}
                </div>
              </td>
            </tr>
            {isCollapsed ? null : group.items.map((v) => renderRow(v))}
          </tbody>
        );
      })}
    </>
  );
}
